/**
 * 分类素材 SWR（Stale-While-Revalidate）内存缓存与 Hover 预取引擎
 * 
 * 核心功能：
 * 1. LRU 内存缓存池（保留最近 40 个分类组合，0ms 极速直出）
 * 2. 稳定参数指纹计算（精确匹配多维分类、标签与筛选组合）
 * 3. 飞行中请求防并发合并（In-flight Deduplication，避免高频 Hover 重复发包）
 * 4. SWR 静默校验机制（后台轻量比对，无感热更最新数据）
 */

import { get_api_data } from "@/utils/ajax";

// 缓存池最大容量（防止长时间驻留占用过多内存）
const MAX_CACHE_SIZE = 40;

// 缓存有效新鲜度阈值（毫秒）：10 分钟
const CACHE_FRESH_TIME = 10 * 60 * 1000;

class CategoryCacheEngine {
    constructor() {
        // LRU 缓存 Map（按使用频率与时序维护）
        this.cacheMap = new Map();
        // 飞行中请求字典（key => Promise），用于合并重复请求
        this.inFlightRequests = new Map();
    }

    /**
     * 生成稳定且唯一的缓存键
     * @param {Object} params 筛选查询参数
     * @returns {string} 缓存键
     */
    generateKey(params = {}) {
        const driveIds = Array.isArray(params.driveIds)
            ? [...params.driveIds].sort().join(",")
            : (params.driveIds || "");
        const lv1 = Array.isArray(params.lv1_categories)
            ? [...params.lv1_categories].sort().join(",")
            : (params.lv1_categories || "");
        const lv2 = Array.isArray(params.lv2_categories)
            ? [...params.lv2_categories].sort().join(",")
            : (params.lv2_categories || "");
        const type = params.type || "all";
        const size = params.size || "all";
        const scenes = params.scenes !== undefined && params.scenes !== null ? String(params.scenes) : "0";
        const keyword = params.keyword ? String(params.keyword).trim() : "";
        const orderby = params.orderby || "created_at";
        const order = params.order || "desc";
        const page = params.page || 1;

        return `cat_${driveIds}|lv1_${lv1}|lv2_${lv2}|typ_${type}|sz_${size}|sc_${scenes}|kw_${keyword}|ord_${orderby}_${order}|p_${page}`;
    }

    /**
     * 读取缓存数据（LRU 命中时提升至最新）
     * @param {Object} params 查询参数
     * @returns {Object|null} 缓存对象 { data, meta, timestamp }
     */
    get(params) {
        const key = this.generateKey(params);
        if (!this.cacheMap.has(key)) {
            return null;
        }

        const item = this.cacheMap.get(key);
        // LRU 策略：先删后插，更新其在 Map 中的最新活跃位置
        this.cacheMap.delete(key);
        this.cacheMap.set(key, item);

        return item;
    }

    /**
     * 写入缓存（自动淘汰最旧条目）
     * @param {Object} params 查询参数
     * @param {Array} data 卡片列表数据
     * @param {Object} meta 分页与总数元数据
     */
    set(params, data, meta) {
        if (!Array.isArray(data)) return;

        const key = this.generateKey(params);

        // 如果已存在先移除，保持插入顺序最新
        if (this.cacheMap.has(key)) {
            this.cacheMap.delete(key);
        } else if (this.cacheMap.size >= MAX_CACHE_SIZE) {
            // 容量超限，淘汰最久未访问的条目（即首个 key）
            const oldestKey = this.cacheMap.keys().next().value;
            this.cacheMap.delete(oldestKey);
        }

        this.cacheMap.set(key, {
            data: [...data],
            meta: meta ? { ...meta } : { total: data.length },
            timestamp: Date.now(),
            key
        });
    }

    /**
     * 规范化构建接口请求参数
     */
    normalizeQueryParams(params = {}) {
        const driveIds = Array.isArray(params.driveIds)
            ? params.driveIds
            : (params.driveIds ? [params.driveIds] : []);
        const lv1_categories = Array.isArray(params.lv1_categories)
            ? params.lv1_categories
            : (params.lv1_categories ? [params.lv1_categories] : []);
        const lv2_categories = Array.isArray(params.lv2_categories)
            ? params.lv2_categories
            : (params.lv2_categories ? [params.lv2_categories] : []);

        return {
            driveIds,
            lv1_categories,
            lv2_categories,
            type: params.type || "all",
            size: params.size || "all",
            start: params.start || "",
            end: params.end || "",
            keyword: params.keyword || "",
            scenes: params.scenes !== undefined && params.scenes !== null ? params.scenes : 0,
            inc_tag: params.inc_tag || "include",
            orderby: params.orderby || "created_at",
            order: params.order || "desc",
            per_page: params.per_page || 80,
            page: params.page || 1,
        };
    }

    /**
     * 智能预取某个分类的第一页数据（Hover 预取或后台静默拉取）
     * @param {Object} params 查询参数
     * @returns {Promise<Object|null>}
     */
    async prefetch(params = {}) {
        const key = this.generateKey(params);

        // 1. 若已有新鲜缓存（在有效新鲜时间内），直接返回缓存
        const cached = this.get(params);
        if (cached && (Date.now() - cached.timestamp < CACHE_FRESH_TIME)) {
            return Promise.resolve(cached);
        }

        // 2. 若当前已有相同请求在飞行中（In-Flight），直接复用该 Promise，不发重复网络包
        if (this.inFlightRequests.has(key)) {
            return this.inFlightRequests.get(key);
        }

        // 3. 构建标准网络请求
        const queryParams = this.normalizeQueryParams(params);
        const fetchPromise = (async () => {
            try {
                const response = await get_api_data("GET", "search", queryParams);
                if (response && response.data && Array.isArray(response.data.data)) {
                    this.set(params, response.data.data, response.data.meta);
                    return {
                        data: response.data.data,
                        meta: response.data.meta,
                        timestamp: Date.now()
                    };
                }
                return null;
            } catch (err) {
                // 静默预取，失败不打扰主流程
                return null;
            } finally {
                this.inFlightRequests.delete(key);
            }
        })();

        this.inFlightRequests.set(key, fetchPromise);
        return fetchPromise;
    }

    /**
     * SWR 静默校验：在命中缓存直出后，异步拉取最新数据，若有变化平滑更新
     * @param {Object} params 查询参数
     * @param {Function} onUpdate 回调函数，仅当数据存在真实变更时触发 onUpdate(newData, newMeta)
     */
    async revalidate(params = {}, onUpdate) {
        const key = this.generateKey(params);
        const cached = this.get(params);

        // 20 秒内刚存入的数据无需立即重复拉取校验，节约带宽与服务器压力
        if (cached && (Date.now() - cached.timestamp < 20 * 1000)) {
            return;
        }

        try {
            const queryParams = this.normalizeQueryParams(params);
            const response = await get_api_data("GET", "search", queryParams);

            if (response && response.data && Array.isArray(response.data.data)) {
                const freshData = response.data.data;
                const freshMeta = response.data.meta;

                // 智能比对新旧数据是否发生变化（首条ID或总条数差异）
                let hasChanges = false;
                if (!cached) {
                    hasChanges = true;
                } else {
                    const cachedData = cached.data || [];
                    const cachedMeta = cached.meta || {};

                    const freshTotal = freshMeta ? freshMeta.total : freshData.length;
                    const cachedTotal = cachedMeta ? cachedMeta.total : cachedData.length;

                    const freshFirstId = freshData[0] ? freshData[0].fileId : null;
                    const cachedFirstId = cachedData[0] ? cachedData[0].fileId : null;

                    if (freshTotal !== cachedTotal || freshFirstId !== cachedFirstId) {
                        hasChanges = true;
                    }
                }

                // 更新内存池
                this.set(params, freshData, freshMeta);

                // 若确实有更新且传入了回调，则平滑通知上层组件更新
                if (hasChanges && typeof onUpdate === "function") {
                    onUpdate(freshData, freshMeta);
                }
            }
        } catch (err) {
            // 静默校验异常无需报错
        }
    }

    /**
     * 清空全部或指定缓存
     */
    clear(key = null) {
        if (key) {
            this.cacheMap.delete(key);
        } else {
            this.cacheMap.clear();
        }
    }
}

// 导出全局单例
export const categoryCache = new CategoryCacheEngine();
export default categoryCache;

